import { getMediaUrl, PddPayloadTypes } from "@/constants";
import { Context } from "@/context";
import { DieEventValues } from "@/enums";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import type { Manifest, Trigger } from "@/types";
import { useFrame } from "@react-three/fiber";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  type RefObject,
} from "react";

export const useSoundEventHandler = (
  id: string,
  currentFrame: RefObject<number>,
  manifest: Manifest,
) => {
  const { dieType, lastStandardEvent, frameData } = useRenderedDiceStore(
    (state) => state.renderedDice.entities[id],
  );
  const { onDiceEvent, offDiceEvent } = useRenderedDiceStore();
  const volume = useRenderedDiceStore((state) => state.userSettings?.volume);
  const soundsObj = manifest.resources?.sounds;
  const soundsArray: string[] = Object.values(
    soundsObj ?? [
      "sounds/collision-1.wav",
      "sounds/collision-2.wav",
      "sounds/collision-3.wav",
    ],
  );
  const { nodeEnv: ne, env: e, renderedWorkerPort } = useContext(Context);
  const messenger: Pick<typeof self, "postMessage"> =
    renderedWorkerPort ?? self;
  const nameJson = self.name ? JSON.parse(self.name) : {};
  const nodeEnv = ne || nameJson.nodeEnv;
  const env = e || nameJson.env;
  const mediaUrl = getMediaUrl(nodeEnv, env);
  const lastEventTypePlayedRef = useRef<string | null>(null);
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);

  const runSoundsForEvent = useCallback(
    (event?: string) => {
      if (!volume || !event || lastEventTypePlayedRef.current === event) return;

      const sounds = manifest[dieType].sounds?.map((trigger) => {
        if (Array.isArray(trigger)) {
          const randomIndex = Math.floor(Math.random() * trigger.length);
          return trigger[randomIndex];
        }
        return trigger;
      }) as Trigger[];
      const soundKey = sounds?.find(
        ({ triggerStart }) => triggerStart === event,
      )?.definition;

      if (!soundKey) return;

      const targetSound = manifest.resources.sounds[soundKey];
      const soundIndex = soundsArray.findIndex(
        (sound) => sound === targetSound,
      );

      const soundPath = soundsArray[soundIndex];
      const soundUrl = customUrlMap
        ? customUrlMap[soundPath]
        : `${mediaUrl}/dice/bundles/${manifest.slug}/${soundPath}`;

      messenger.postMessage({
        type: PddPayloadTypes.PlaySound,
        payload: {
          url: soundUrl,
          volume: 1,
        },
      });

      lastEventTypePlayedRef.current = event;
    },
    [customUrlMap, dieType, manifest, mediaUrl, messenger, soundsArray, volume],
  );

  useEffect(() => {
    if (!(volume && onDiceEvent)) return;

    const handleEvent = ({
      id: eventId,
      trigger,
    }: {
      id: string;
      trigger?: string;
    }) => {
      if (eventId !== id) return;

      runSoundsForEvent(trigger);
    };

    onDiceEvent("event", handleEvent);

    return () => {
      if (offDiceEvent) {
        offDiceEvent("event", handleEvent);
      }
    };
  }, [id, offDiceEvent, onDiceEvent, runSoundsForEvent, volume]);

  useFrame(() => {
    if (
      !volume ||
      lastStandardEvent === DieEventValues.rollComplete ||
      lastStandardEvent === DieEventValues.minRoll ||
      lastStandardEvent === DieEventValues.maxRoll ||
      lastStandardEvent === DieEventValues.dieRemoved
    ) {
      return;
    }

    // Changes in collisionVolume indicate a new collision event
    const frameVolume = frameData?.[currentFrame.current]?.collisionVolume;

    if (!frameVolume) return;

    const collisionIndices = soundsArray
      .map((sound, i) => (sound.toLowerCase().includes("collision") ? i : -1))
      .filter((i) => i !== -1);
    const collisionIndex =
      collisionIndices[Math.floor(Math.random() * collisionIndices.length)];

    const soundPath = soundsArray[collisionIndex];
    const soundUrl = customUrlMap
      ? customUrlMap[soundPath]
      : `${mediaUrl}/dice${soundsObj ? `/bundles/${manifest.slug}` : ""}/${soundPath}`;

    messenger.postMessage({
      type: PddPayloadTypes.PlaySound,
      payload: {
        url: soundUrl,
        volume: frameVolume,
        isCollision: true,
      },
    });
  });
};
